import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Alert, Button, ButtonGroup, Col, Form, Modal, OverlayTrigger, Row, Table, ToggleButton, Tooltip } from 'react-bootstrap';
import { ErrorMessage, Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { faInfoCircle, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { restValidationToForm } from '../utils/formUtils';
import { Attachment, ExtraProducts, FileType, ParentInfo } from '../api/account.types';
import { deleteFile, getFiles, getFile, updateChild, uploadFile, getSchoolFoodTypes } from '../api/registrations';
import { dateFormatter, nth } from '../utils/formatters';
import FileInput from '../components/FileInput';

interface Values {
  registration_id: number;
  school_id: number;
  ssn: string;
  intolerance: string | undefined;
  intolerance_reason: string;
  allergies: string | undefined;
  allergies_reason: string;
  products: boolean[];
  food_types: number | string | undefined;
  payer_email: string;
  payer_ssn: string;
}

interface Props {
  onClose: (created: boolean) => void;
  originalValues: any;
  parentInfo: ParentInfo;
}
const EditRegisteredChildModal = ({ originalValues, parentInfo, onClose }: Props): React.ReactElement => {
  const { t, i18n } = useTranslation(['registerChildModal', 'common']);
  const [files, setFiles] = useState<any[]>([]);
  const [originalFiles, setOriginalFiles] = useState<any[]>();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const registrationID = originalValues.registrations[0].registration_id;
  const [schoolFoodTypes, setSchoolFoodTypes] = useState<any[]>();
  const validationSchema = useMemo(() => {
    return Yup.object().shape({
      allergies: Yup.string().required(t('errors.errorMessage')),
      allergies_reason: Yup.string().when('allergies', {
        is: '1',
        then: Yup.string().required(t('errors.emptyInputAllergies')),
        otherwise: Yup.string(),
      }),
      intolerance: Yup.string().required(t('errors.errorMessage')),
      intolerance_reason: Yup.string().when('intolerance', {
        is: '1',
        then: Yup.string().required(t('errors.emptyInputIntolerance')),
        otherwise: Yup.string(),
      }),
    });
  }, [t, i18n.language]);

  const initialValues: Values = {
    registration_id: originalValues.registrations[0].registration_id,
    school_id: originalValues.school_id,
    ssn: originalValues.ssn,
    intolerance: originalValues.registrations[0].intolerance,
    intolerance_reason: originalValues.registrations[0].intolerance_reason ?? '',
    allergies: originalValues.registrations[0].allergies,
    allergies_reason: originalValues.registrations[0].allergies_reason ?? '',
    products:
      originalValues.extra_products.reduce((acc: Record<number, boolean>, curr: any) => {
        acc[curr.product_id] = curr.is_registered;
        return acc;
      }, {}) || {},
    payer_email: originalValues.registrations[0].payer_email,
    payer_ssn: originalValues.registrations[0].payer_ssn,
    food_types: originalValues.registrations[0].food_type_school_id !== null ? originalValues.registrations[0].food_type_school_id : undefined,
  };

  const loadSchoolFoodTypes = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await getSchoolFoodTypes(originalValues.school_id);
      setSchoolFoodTypes(response.data.items);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [originalValues.school_id]);

  useEffect(() => {
    loadSchoolFoodTypes();
  }, [loadSchoolFoodTypes]);

  const radios = [
    { name: t('yes'), value: '1' },
    { name: t('no'), value: '0' },
  ];

  const loadOriginalFiles = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await getFiles({ registration_id: registrationID, customer_id: originalValues.customer });
      setOriginalFiles(response.data.items);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);
  const loadFile = useCallback(async (attachment: Attachment) => {
    setLoading(true);
    setError(false);
    try {
      const response = await getFile(attachment, originalValues.customer);
      const link = document.createElement('a');
      const file = new Blob([response.data], { type: response.headers.contentType });
      const url = URL.createObjectURL(file);
      link.href = url;
      link.download = attachment.filename;

      document.body.appendChild(link);

      link.click();
      setTimeout(function () {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 0);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);
  const removeOriginalFile = useCallback(async (id: number) => {
    setLoading(true);
    setError(false);
    try {
      await deleteFile(id);
      loadOriginalFiles();
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOriginalFiles();
  }, [loadOriginalFiles]);

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        // enableReinitialize
        onSubmit={async (values: Values, { setSubmitting, setFieldError }: FormikHelpers<Values>) => {
          try {
            if (files.length > 0) {
              const modifiedItems = files.map((item: FileType) => {
                return {
                  registration_id: values.registration_id,
                  student_id: originalValues.student_id,
                  name: item.name,
                  filename: item.name,
                  mime: item.type && item.type.length > 0 ? item.type : 'application/octet-stream',
                  body: item.data,
                  customer: originalValues.customer,
                };
              });
              const promises = modifiedItems.map((i) => {
                return uploadFile(i);
              });
              await Promise.all(promises);
            }
            await updateChild({
              ...values,
              products: JSON.stringify(
                Object.entries(values.products)
                  .filter(([, v]) => v)
                  .map(([k]) => ({ product_id: Number(k) }))
              ),
              food_types: values.food_types === 'undefined' ? null : values.food_types,
            });

            toast.success(t('studentEdited'));
            onClose(true);
          } catch (e) {
            restValidationToForm({
              t,
              errorResponse: e.response,
              setFieldError,
              errorFieldMap: {
                name: 'name',
              },
            });
            toast.error(`${t('common:informationError')}. ${e.response.data.error}.`);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ handleReset, handleSubmit, setStatus, values, setFieldValue, getFieldProps, errors }) => (
          <Modal show onHide={() => onClose(false)} backdrop="static" keyboard={false} size="lg">
            <Modal.Header closeButton>
              <Modal.Title>{t('editTitle')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form
                onReset={handleReset}
                onSubmit={(e: React.FormEvent<HTMLFormElement>): void => {
                  setStatus('submitted');
                  handleSubmit(e);
                }}
              >
                <Row>
                  <Col>
                    <Form.Group controlId="name">
                      <Form.Text>{t('name')}</Form.Text>
                      <Form.Label className="margin-bottom-0">{originalValues.name}</Form.Label>
                      <Form.Text className="text-muted text-indent margin-top-0">{initialValues.ssn}</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group controlId="school">
                      <Form.Text>{t('school')}</Form.Text>
                      <Form.Label className="margin-bottom-0">{originalValues.school_name}</Form.Label>
                    </Form.Group>
                  </Col>
                </Row>
                <hr />
                <Row>
                  <Col>
                    <Form.Group controlId="registration">
                      <Form.Text>{t('registration')}</Form.Text>
                      <Form.Check type="checkbox" id="registration" name="registration" disabled label={t('schoolMeals')} checked={originalValues.registrations} />
                    </Form.Group>
                    {schoolFoodTypes && schoolFoodTypes.length > 0 && (
                      <Form.Group>
                        <Form.Label>{t('specialFoodType')}</Form.Label>
                        <Form.Control
                          as="select"
                          value={values.food_types}
                          onChange={(event) => {
                            setFieldValue('food_types', event.target.value);
                          }}
                        >
                          <option key="undefined" value="undefined">
                            {t('noSpecialFoodTypeRequested')}
                          </option>
                          {schoolFoodTypes?.map((item: any) => {
                            return (
                              <option key={item.food_type_school_id} value={item.food_type_school_id}>
                                {i18n.language === 'en' ? item.food_type_en : item.food_type_is}
                              </option>
                            );
                          })}
                        </Form.Control>
                      </Form.Group>
                    )}
                  </Col>
                  <Col>
                    <Form.Group controlId="extra">
                      {originalValues?.extra_products?.length > 0 && (
                        <>
                          <Form.Text>{t('extra')}</Form.Text>
                        </>
                      )}
                      {originalValues?.extra_products?.map((extra: ExtraProducts) => {
                        return (
                          <Form.Check
                            type="checkbox"
                            key={extra.product_id}
                            id={`products.${extra.product_id}`}
                            label={extra.product_name}
                            onChange={(event) => {
                              setFieldValue(`products.${extra.product_id}`, event.currentTarget.checked);
                            }}
                            disabled={extra.end_date !== null}
                            checked={values.products[extra.product_id]}
                          />
                        );
                      })}
                    </Form.Group>
                  </Col>
                </Row>
                <hr />
                <Row>
                  <Col>
                    <Form.Group controlId="payer">
                      <Form.Text>{t('payer')}</Form.Text>
                      <Form.Label className="margin-bottom-0">{originalValues.registrations[0].payer_name}</Form.Label>
                      <Form.Text className="text-muted text-indent margin-top-0">{originalValues.registrations[0].payer_ssn}</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group>
                      <Form.Label>{t('addEmail')}</Form.Label>
                      <Form.Control type="email" disabled={originalValues.registrations[0].payer_ssn !== parentInfo.ssn} {...getFieldProps('payer_email')} />
                      <ErrorMessage name="payer_email" component="div" className="invalid-feedback d-block text-left" />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md="12">
                    {!originalValues.registrations[0].payer_email && originalValues.registrations[0].payer_ssn !== parentInfo.ssn && (
                      <Alert variant="warning" className="alert-box">
                        <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '5px' }} />
                        <span>{t('registerChildModal:payerssnError')}</span>
                      </Alert>
                    )}
                  </Col>
                </Row>
                <hr />
                <Row>
                  <Col>
                    <Form.Group className="allergiesRadio">
                      <Form.Label>{t('childHasAllergies')}</Form.Label>
                      <ButtonGroup toggle>
                        {radios.map((radio) => (
                          <ToggleButton
                            key={radio.value}
                            type="radio"
                            variant="secondary"
                            name="allergies"
                            className="buttonHover"
                            value={radio.value}
                            checked={values.allergies === radio.value}
                            onChange={(e) => {
                              setFieldValue('allergies', e.currentTarget.value);
                              if (e.currentTarget.value === '0') {
                                setFieldValue('allergies_reason', '');
                              }
                            }}
                          >
                            {radio.name}
                          </ToggleButton>
                        ))}
                      </ButtonGroup>
                      <ErrorMessage name="allergies" component="div" className="invalid-feedback d-block text-left" />
                      {values.allergies === '1' && (
                        <>
                          {originalValues.customer === 21 && (
                            <Alert variant="warning" className="alert-box">
                              <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '5px' }} />
                              <span>{t('registerChildModal:allergiesIntoloranceInfoText')}</span>
                            </Alert>
                          )}
                          <Form.Group>
                            <Form.Control type="text" {...getFieldProps('allergies_reason')} />
                            <ErrorMessage name="allergies_reason" component="div" className="invalid-feedback d-block text-left" />
                          </Form.Group>
                        </>
                      )}
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group className="allergiesRadio">
                      <Form.Label>{t('childHasIntolerance')}</Form.Label>
                      <ButtonGroup toggle>
                        {radios.map((radio) => (
                          <ToggleButton
                            key={radio.value}
                            type="radio"
                            variant="secondary"
                            name="intolerance"
                            className="buttonHover"
                            value={radio.value}
                            checked={values.intolerance === radio.value}
                            onChange={(e) => {
                              setFieldValue('intolerance', e.currentTarget.value);
                              if (e.currentTarget.value === '0') {
                                setFieldValue('intolerance_reason', '');
                              }
                            }}
                          >
                            {radio.name}
                          </ToggleButton>
                        ))}
                      </ButtonGroup>
                      <ErrorMessage name="intolerance" component="div" className="invalid-feedback d-block text-left" />
                      {values.intolerance === '1' && (
                        <>
                          {originalValues.customer === 21 && (
                            <Alert variant="warning" className="alert-box">
                              <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '5px' }} />
                              <span>{t('registerChildModal:allergiesIntoloranceInfoText')}</span>
                            </Alert>
                          )}
                          <Form.Group>
                            <Form.Control type="text" {...getFieldProps('intolerance_reason')} />
                            <ErrorMessage name="intolerance_reason" component="div" className="invalid-feedback d-block text-left" />
                          </Form.Group>
                        </>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
                <hr />
                <Row>
                  {(values.intolerance === '1' || values.allergies === '1') && (
                    <>
                      <Col>
                        <FileInput setFiles={setFiles} files={files} />
                      </Col>
                      <Col>
                        <Table style={{ margin: 0 }} responsive>
                          <thead>
                            <tr>
                              <th>{t('attachment')}</th>
                              <th />
                            </tr>
                          </thead>
                          <tbody>
                            {files.map((item: FileType) => {
                              return (
                                <tr>
                                  <td>{item.name}</td>
                                  <td>
                                    <Button variant="link" onClick={() => setFiles(files.filter((i) => i.name !== item.name))} style={{ float: 'right' }}>
                                      <FontAwesomeIcon icon={faTrashAlt} style={{ color: '#e74a3b' }} />
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}
                            {originalFiles &&
                              originalFiles.map((item: Attachment, idx) => {
                                return (
                                  <tr>
                                    <td>
                                      <OverlayTrigger
                                        placement="top"
                                        delay={{ show: 250, hide: 400 }}
                                        overlay={
                                          <Tooltip id="button-tooltip">
                                            <div>
                                              <span>Skráð af: {item.created_by_name} </span>
                                              <span>Dags: {dateFormatter(item.created_date)}</span>
                                            </div>
                                          </Tooltip>
                                        }
                                      >
                                        <Button variant="link" onClick={() => loadFile(item)} style={{ paddingLeft: 0, wordBreak: 'break-all' }}>
                                          {item.name}
                                        </Button>
                                      </OverlayTrigger>
                                    </td>
                                    <td>
                                      <Button variant="link" onClick={() => removeOriginalFile(item.attachment_id)} style={{ float: 'right' }}>
                                        <FontAwesomeIcon icon={faTrashAlt} style={{ color: '#e74a3b' }} />
                                      </Button>
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </Table>
                      </Col>
                    </>
                  )}
                </Row>
                <hr />
              </Form>
              {originalValues.resignation_cutoff === 0 && originalValues.extra_products.length > 0 && (
                <Alert variant="warning">
                  <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '5px' }} />
                  <span>{t('resignationInformation2')}</span>
                </Alert>
              )}
              {originalValues.resignation_cutoff !== 0 && originalValues.extra_products.length > 0 && (
                <Alert variant="warning">
                  <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '5px' }} />
                  <span>{t('resignationInformation', { day: originalValues.resignation_cutoff, nth: nth(originalValues.resignation_cutoff) })}</span>
                </Alert>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="primary"
                type="submit"
                onClick={() => {
                  setStatus('submitted');
                  handleSubmit();
                }}
              >
                {t('common:submit')}
              </Button>
              <Button variant="secondary" onClick={() => onClose(false)}>
                {t('common:cancel')}
              </Button>
            </Modal.Footer>
          </Modal>
        )}
      </Formik>
    </>
  );
};

export default EditRegisteredChildModal;
